import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Front-desk "Edit Appointment" dialog — lets staff correct the date/time/type/
// notes of an appointment that is still SCHEDULED or WAITING. Does not touch
// status or the doctor's check-in queue position.
export default function EditAppointmentDialog({ appointment, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ appointment_date: "", appointment_time: "", appointment_type: "NEW_VISIT", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (appointment) {
      setForm({
        appointment_date: appointment.appointment_date,
        appointment_time: appointment.appointment_time,
        appointment_type: appointment.appointment_type,
        notes: appointment.notes || "",
      });
    }
  }, [appointment]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/appointments/${appointment.id}`, form);
      toast.success("Appointment updated");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!appointment} onOpenChange={onOpenChange}>
      <DialogContent data-testid="edit-appointment-modal">
        <DialogHeader>
          <DialogTitle>Edit Appointment</DialogTitle>
          <DialogDescription>Update the date, time, type or notes for {appointment?.patient_name}'s appointment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Appointment Date</Label>
            <DatePicker testId="edit-appt-date" value={form.appointment_date} onChange={(v) => setForm((f) => ({ ...f, appointment_date: v }))} />
          </div>
          <div>
            <Label>Appointment Time</Label>
            <Input data-testid="edit-appt-time" type="time" value={form.appointment_time} onChange={(e) => setForm((f) => ({ ...f, appointment_time: e.target.value }))} />
          </div>
          <div>
            <Label>Appointment Type</Label>
            <Select value={form.appointment_type} onValueChange={(v) => setForm((f) => ({ ...f, appointment_type: v }))}>
              <SelectTrigger data-testid="edit-appt-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NEW_VISIT">New Visit</SelectItem>
                <SelectItem value="FOLLOW_UP">Follow-up</SelectItem>
                <SelectItem value="EMERGENCY">Emergency</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="edit-appt-notes" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="edit-appt-save-btn" className="bg-teal-700 hover:bg-teal-800" disabled={saving || !form.appointment_date || !form.appointment_time} onClick={save}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
